import PropTypes from "prop-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { Close, ExpandMore, NotificationsOutlined } from "@mui/icons-material";
import { criticalAlertSx } from "../model/alertStyles";
import { formatAlertDate } from "../model/alertsModel";

function DetailField({ label, value, wide = false, mono = false }) {
  return (
    <Grid item xs={12} sm={wide ? 12 : 6}>
      <Typography component="dt" sx={{ color: "#526579", fontSize: "0.75rem", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography
        component="dd"
        sx={{
          m: 0,
          color: "#243b53",
          fontSize: "0.875rem",
          fontWeight: 500,
          lineHeight: 1.6,
          overflowWrap: "anywhere",
          ...(mono && { fontFamily: "monospace" }),
        }}
      >
        {String(value == null || value === "" ? "Sin información" : value)}
      </Typography>
    </Grid>
  );
}
DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  wide: PropTypes.bool,
  mono: PropTypes.bool,
};

function DetailSection({ title, children }) {
  return (
    <Box sx={{ border: "1px solid #dce5ed", borderRadius: 2, bgcolor: "#fff", p: 2 }}>
      <Typography
        component="h3"
        sx={{ color: "#193e54", fontSize: "0.875rem", fontWeight: 700, mb: 1.5 }}
      >
        {title}
      </Typography>
      <Grid component="dl" container spacing={1.5} sx={{ mb: 0 }}>
        {children}
      </Grid>
    </Box>
  );
}
DetailSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default function AlertDetails({ alert, onClose }) {
  return (
    <Dialog
      open={Boolean(alert)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="alert-detail-title"
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "92vh", m: { xs: 1, sm: 3 } } }}
    >
      <DialogTitle
        component="div"
        sx={{ display: "flex", alignItems: "center", gap: 1.5, bgcolor: "#193e54", px: 3, py: 2 }}
      >
        <Box
          sx={{
            display: "flex",
            p: 1,
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,.12)",
            color: "#fff",
          }}
        >
          <NotificationsOutlined />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            id="alert-detail-title"
            component="h2"
            sx={{ color: "#fff", fontSize: "1rem", fontWeight: 700 }}
          >
            Detalle de la alerta #{alert?.recordId || "—"}
          </Typography>
          <Typography sx={{ color: "#d8e8f1", fontSize: "0.75rem", mt: 0.25 }}>
            Información del equipo y del evento
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Cerrar detalle de alerta" sx={{ color: "#fff" }}>
          <Close />
        </IconButton>
      </DialogTitle>
      {alert && (
        <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f5f7fa" }}>
          <Box sx={{ py: 2.5, px: 1.5, borderRadius: 2, ...criticalAlertSx(alert) }}>
            <Typography
              component="h3"
              sx={{ color: "#243b53", fontSize: "1.5rem", fontWeight: 700, mb: 1.5 }}
            >
              {alert.message}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Chip
                label={`Prioridad: ${alert.priorityText}${
                  alert.priority != null ? ` (${alert.priority})` : ""
                }`}
                size="small"
                sx={{ bgcolor: "#e6edf5", color: "#243b53" }}
              />
            </Box>
          </Box>
          <Box sx={{ display: "grid", gap: 2 }}>
            <DetailSection title="Equipo y ubicación">
              <DetailField label="Comercio" value={alert.commerce} />
              <DetailField label="Equipo" value={alert.equipment} />
              <DetailField label="IP" value={alert.ip} mono />
              <DetailField label="ID del host" value={alert.hostId} mono />
            </DetailSection>
            <Box
              sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}
            >
              <DetailSection title="Clasificación del evento">
                <DetailField label="Tipo" value={alert.alertType} wide />
                <DetailField
                  wide
                  label="Severidad de metadata · valor original"
                  value={alert.metadataSeverity}
                />
              </DetailSection>
              <DetailSection title="Fechas y trazabilidad">
                <DetailField
                  wide
                  label="Fecha de creación · Bogotá"
                  value={formatAlertDate(alert.timestamp)}
                />
                <DetailField
                  wide
                  label="Actualización del registro · Bogotá"
                  value={formatAlertDate(alert.rawData.updatedAt)}
                />
                <DetailField label="ID del evento" value={alert.eventId} mono wide />
              </DetailSection>
            </Box>
            <Accordion
              key={alert.id}
              defaultExpanded={false}
              elevation={0}
              disableGutters
              sx={{
                border: "1px solid #dce5ed",
                borderRadius: "8px !important",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: "#526579" }} />}
                sx={{ px: 2.5 }}
              >
                <Typography sx={{ color: "#354d63", fontSize: "0.8rem", fontWeight: 600 }}>
                  Datos originales del evento
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Box
                  component="pre"
                  sx={{
                    p: 2,
                    m: 0,
                    bgcolor: "#f5f7fa",
                    color: "#243b53",
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    maxHeight: 240,
                    overflow: "auto",
                  }}
                >
                  {JSON.stringify(alert.rawData, null, 2)}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
      )}
      <DialogActions sx={{ px: 3, py: 1.5, borderTop: "1px solid #dce5ed", bgcolor: "#fff" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ color: "#193e54", borderColor: "#c6d3df" }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
AlertDetails.propTypes = { alert: PropTypes.object, onClose: PropTypes.func.isRequired };
